/**
 * Maps blog post hero filenames to per-locale public OG image URLs.
 *
 * One 1200×630 social card per post per locale (CWS-118), rendered by
 * `scripts/generate-blog-social-cards.mjs` into `public/og/blog/` and committed
 * — the headline is burned into the art in the reader's language, so a German
 * reader shares a German card. The card path is derived from the hero
 * filename's stem plus the locale (`<stem>-<lang>.png`);
 * `tests-node/blogSocialCards.test.ts` holds every value against that
 * derivation, so a hand-typo in any of the 115 paths is a named test failure
 * rather than a 404ing `og:image`.
 *
 * Keys are the hero image **filename** from `src/data/blogPosts.ts` — not the
 * post slug. `getOgImageForRoute` resolves the post from the slug first, then
 * looks the hero up here.
 */
import type { SeoLanguage } from "../lib/seoMetadata";

export const blogOgImageMap: Record<string, Record<SeoLanguage, string>> = {
  "claude-code-quality-hero.jpg": {
    en: "/og/blog/claude-code-quality-hero-en.png",
    de: "/og/blog/claude-code-quality-hero-de.png",
    fr: "/og/blog/claude-code-quality-hero-fr.png",
    it: "/og/blog/claude-code-quality-hero-it.png",
    es: "/og/blog/claude-code-quality-hero-es.png",
  },
  "vendorland-recognition.png": {
    en: "/og/blog/vendorland-recognition-en.png",
    de: "/og/blog/vendorland-recognition-de.png",
    fr: "/og/blog/vendorland-recognition-fr.png",
    it: "/og/blog/vendorland-recognition-it.png",
    es: "/og/blog/vendorland-recognition-es.png",
  },
  "tgi-llama33-hero.png": {
    en: "/og/blog/tgi-llama33-hero-en.png",
    de: "/og/blog/tgi-llama33-hero-de.png",
    fr: "/og/blog/tgi-llama33-hero-fr.png",
    it: "/og/blog/tgi-llama33-hero-it.png",
    es: "/og/blog/tgi-llama33-hero-es.png",
  },
  "buy-vs-build-hero.png": {
    en: "/og/blog/buy-vs-build-hero-en.png",
    de: "/og/blog/buy-vs-build-hero-de.png",
    fr: "/og/blog/buy-vs-build-hero-fr.png",
    it: "/og/blog/buy-vs-build-hero-it.png",
    es: "/og/blog/buy-vs-build-hero-es.png",
  },
  "llama-3-2-hero.png": {
    en: "/og/blog/llama-3-2-hero-en.png",
    de: "/og/blog/llama-3-2-hero-de.png",
    fr: "/og/blog/llama-3-2-hero-fr.png",
    it: "/og/blog/llama-3-2-hero-it.png",
    es: "/og/blog/llama-3-2-hero-es.png",
  },
  "explore-ai-hero.png": {
    en: "/og/blog/explore-ai-hero-en.png",
    de: "/og/blog/explore-ai-hero-de.png",
    fr: "/og/blog/explore-ai-hero-fr.png",
    it: "/og/blog/explore-ai-hero-it.png",
    es: "/og/blog/explore-ai-hero-es.png",
  },
  "swift-hero.png": {
    en: "/og/blog/swift-hero-en.png",
    de: "/og/blog/swift-hero-de.png",
    fr: "/og/blog/swift-hero-fr.png",
    it: "/og/blog/swift-hero-it.png",
    es: "/og/blog/swift-hero-es.png",
  },
  "unsloth-hero.png": {
    en: "/og/blog/unsloth-hero-en.png",
    de: "/og/blog/unsloth-hero-de.png",
    fr: "/og/blog/unsloth-hero-fr.png",
    it: "/og/blog/unsloth-hero-it.png",
    es: "/og/blog/unsloth-hero-es.png",
  },
  "lora-hero.png": {
    en: "/og/blog/lora-hero-en.png",
    de: "/og/blog/lora-hero-de.png",
    fr: "/og/blog/lora-hero-fr.png",
    it: "/og/blog/lora-hero-it.png",
    es: "/og/blog/lora-hero-es.png",
  },
  "gemma-multilingual-hero.png": {
    en: "/og/blog/gemma-multilingual-hero-en.png",
    de: "/og/blog/gemma-multilingual-hero-de.png",
    fr: "/og/blog/gemma-multilingual-hero-fr.png",
    it: "/og/blog/gemma-multilingual-hero-it.png",
    es: "/og/blog/gemma-multilingual-hero-es.png",
  },
  "multilingual-llms-hero.png": {
    en: "/og/blog/multilingual-llms-hero-en.png",
    de: "/og/blog/multilingual-llms-hero-de.png",
    fr: "/og/blog/multilingual-llms-hero-fr.png",
    it: "/og/blog/multilingual-llms-hero-it.png",
    es: "/og/blog/multilingual-llms-hero-es.png",
  },
  "llama2-multilingual.png": {
    en: "/og/blog/llama2-multilingual-en.png",
    de: "/og/blog/llama2-multilingual-de.png",
    fr: "/og/blog/llama2-multilingual-fr.png",
    it: "/og/blog/llama2-multilingual-it.png",
    es: "/og/blog/llama2-multilingual-es.png",
  },
  "clutch-awards-2023.png": {
    en: "/og/blog/clutch-awards-2023-en.png",
    de: "/og/blog/clutch-awards-2023-de.png",
    fr: "/og/blog/clutch-awards-2023-fr.png",
    it: "/og/blog/clutch-awards-2023-it.png",
    es: "/og/blog/clutch-awards-2023-es.png",
  },
  "tango-takes-two-hero.png": {
    en: "/og/blog/tango-takes-two-hero-en.png",
    de: "/og/blog/tango-takes-two-hero-de.png",
    fr: "/og/blog/tango-takes-two-hero-fr.png",
    it: "/og/blog/tango-takes-two-hero-it.png",
    es: "/og/blog/tango-takes-two-hero-es.png",
  },
  "competent-management-hero.png": {
    en: "/og/blog/competent-management-hero-en.png",
    de: "/og/blog/competent-management-hero-de.png",
    fr: "/og/blog/competent-management-hero-fr.png",
    it: "/og/blog/competent-management-hero-it.png",
    es: "/og/blog/competent-management-hero-es.png",
  },
  "chatgpt-competitive-advantage-hero.png": {
    en: "/og/blog/chatgpt-competitive-advantage-hero-en.png",
    de: "/og/blog/chatgpt-competitive-advantage-hero-de.png",
    fr: "/og/blog/chatgpt-competitive-advantage-hero-fr.png",
    it: "/og/blog/chatgpt-competitive-advantage-hero-it.png",
    es: "/og/blog/chatgpt-competitive-advantage-hero-es.png",
  },
  "network-effect-hero.png": {
    en: "/og/blog/network-effect-hero-en.png",
    de: "/og/blog/network-effect-hero-de.png",
    fr: "/og/blog/network-effect-hero-fr.png",
    it: "/og/blog/network-effect-hero-it.png",
    es: "/og/blog/network-effect-hero-es.png",
  },
  "ai-development-culture.jpg": {
    en: "/og/blog/ai-development-culture-en.png",
    de: "/og/blog/ai-development-culture-de.png",
    fr: "/og/blog/ai-development-culture-fr.png",
    it: "/og/blog/ai-development-culture-it.png",
    es: "/og/blog/ai-development-culture-es.png",
  },
  "ai-virtuous-cycle.png": {
    en: "/og/blog/ai-virtuous-cycle-en.png",
    de: "/og/blog/ai-virtuous-cycle-de.png",
    fr: "/og/blog/ai-virtuous-cycle-fr.png",
    it: "/og/blog/ai-virtuous-cycle-it.png",
    es: "/og/blog/ai-virtuous-cycle-es.png",
  },
  "blue-river-technology.png": {
    en: "/og/blog/blue-river-technology-en.png",
    de: "/og/blog/blue-river-technology-de.png",
    fr: "/og/blog/blue-river-technology-fr.png",
    it: "/og/blog/blue-river-technology-it.png",
    es: "/og/blog/blue-river-technology-es.png",
  },
  "ai-business-use-case.jpg": {
    en: "/og/blog/ai-business-use-case-en.png",
    de: "/og/blog/ai-business-use-case-de.png",
    fr: "/og/blog/ai-business-use-case-fr.png",
    it: "/og/blog/ai-business-use-case-it.png",
    es: "/og/blog/ai-business-use-case-es.png",
  },
  "ai-designs-dont-work.png": {
    en: "/og/blog/ai-designs-dont-work-en.png",
    de: "/og/blog/ai-designs-dont-work-de.png",
    fr: "/og/blog/ai-designs-dont-work-fr.png",
    it: "/og/blog/ai-designs-dont-work-it.png",
    es: "/og/blog/ai-designs-dont-work-es.png",
  },
  "ai-strategies-hero.png": {
    en: "/og/blog/ai-strategies-hero-en.png",
    de: "/og/blog/ai-strategies-hero-de.png",
    fr: "/og/blog/ai-strategies-hero-fr.png",
    it: "/og/blog/ai-strategies-hero-it.png",
    es: "/og/blog/ai-strategies-hero-es.png",
  },
};
